import React, { useEffect } from "react";
import * as Components from "../../components";
import { NotificationContext } from "../../context/notification-context";
import { TimezoneContext } from "../../context/timezone-context";
import { useFullscreen } from "../../context/fullscreen-context";
import useRequest from "../../api/useRequest";
import {
  UserSettingsRequestFactory,
  UserRequestFactory,
} from "../../api/requestFactory";
import * as Mixpanel from "../../services/mixpanel";
import { identifyUser } from "../../services/fernand";
import BrowsingMemberWorkspaceBanner from "../../components/layout/browsing-member-workspace-banner";
import McpLaunchModal from "../../components/mcp-launch-modal";

function LoggedIn(props) {
  const { requestMaker } = useRequest();
  const { notification, hideNotification, showNotification } =
    React.useContext(NotificationContext);
  const { setTimezone } = React.useContext(TimezoneContext);
  const { isFullscreen } = useFullscreen();
  const [userAccountStatus, setUserAccountStatus] = React.useState(null);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const [settingsResponse, userResponse] = await Promise.all([
          requestMaker(UserSettingsRequestFactory.createGetUserSettingsRequest(true)),
          requestMaker(UserRequestFactory.createGetUserRequest(true)),
        ]);

        setTimezone(settingsResponse.timezone);
        setUserAccountStatus(userResponse);

        // Support widget: identified with the server-signed email.
        identifyUser(userResponse.user, userResponse.fernand);


        // Initialize Mixpanel
        if (userResponse.email) {
          const userName = userResponse.user?.name;
          Mixpanel.identify(userResponse.email, userName);

          // Set people properties for recently created users (within 10 minutes)
          if (userResponse.user?.created_at) {
            const createdAtSeconds = Math.floor(new Date(userResponse.user.created_at).getTime() / 1000);
            const nowSeconds = Math.floor(Date.now() / 1000);
            const tenMinutes = 60 * 10;

            if (createdAtSeconds > nowSeconds - tenMinutes) {
              Mixpanel.set_people({
                $email: userResponse.email,
                $name: userName,
                $created: createdAtSeconds,
              });
            }
          }
        }
      } catch (err) {
        console.error('Error loading user data:', err);
        showNotification(
          {
            title: "Something went wrong",
            content: "Error loading user settings. Please try again later.",
          },
          "error"
        );
      }
    };
    loadUserData();
    return () => {
      setTimezone(null);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestMaker, setTimezone]);

  return (
    <>
      {userAccountStatus && (
        <>
          <Components.Notification
            message={notification.message}
            type={notification.type}
            show={notification.show}
            onClose={hideNotification}
          ></Components.Notification>
          <div className="dynamic-height flex flex-col overflow-hidden">
            {!isFullscreen && <BrowsingMemberWorkspaceBanner />}
            {!isFullscreen && <Components.Layout.TopNavbar />}
            <div
              className={`relative flex flex-1 min-h-0 ${
                !isFullscreen ? "pb-[60px] lg:pb-0" : ""
              } z-0`}
            >
              {!isFullscreen && <Components.Layout.Sidebar />}
              <main
                className={
                  props.isView
                    ? "flex flex-col justify-between flex-1 min-w-0 bg-white lg:border-l lg:border-gray-200 overflow-y-auto"
                    : "flex-1 min-w-0 overflow-y-auto bg-white lg:border-l lg:border-gray-200"
                }
              >
                {!isFullscreen && (
                  <Components.Payments.TrialExpirationReminderHeader
                    userAccountStatus={userAccountStatus}
                  />
                )}
                {props.children}
              </main>
            </div>
          </div>
          {!isFullscreen && <Components.Layout.MobileNav />}
          <Components.GlobalLoader></Components.GlobalLoader>
          <McpLaunchModal />
        </>
      )}
    </>
  );
}

export default LoggedIn;
